// src/components/VariantProfiles.tsx
// Variant Profiles panel - similar to AI Config Profiles in AISettings
import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../context/AuthContext.tsx";
import { CustomVariant } from "../hooks/useCustomVariants.ts";
import { NotificationModal, NotificationState, initialNotificationState } from "./modals/NotificationModal.tsx";
import "./VariantProfiles.css";

const API_URL = process.env.REACT_APP_API_URL || "";
const MAX_CUSTOM_VARIANTS = 12;

interface VariantProfilesProps {
  variants: CustomVariant[];
  onRefresh: () => void;
  onLoadVariant: (variant: CustomVariant) => void;
  currentVariantData: {
    name: string;
    description: string;
    boardSize: number;
    whitePieces: any[];
    blackPieces: any[];
    specialRules: any;
  };
  hasValidConfiguration: boolean;
}

const VariantProfiles: React.FC<VariantProfilesProps> = ({
  variants,
  onRefresh,
  onLoadVariant,
  currentVariantData,
  hasValidConfiguration,
}) => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Modal states
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showOverwriteConfirm, setShowOverwriteConfirm] = useState<CustomVariant | null>(null);

  // Form states
  const [saveName, setSaveName] = useState("");
  const [saveDescription, setSaveDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Notification modal state (replaces native alert dialogs)
  const [notification, setNotification] = useState<NotificationState>(initialNotificationState);

  // Initialize save name from current variant data
  useEffect(() => {
    if (showSaveDialog) {
      setSaveName(currentVariantData.name || "My Custom Variant");
      setSaveDescription(currentVariantData.description || "");
    }
  }, [showSaveDialog, currentVariantData.name, currentVariantData.description]);

  const formatDate = (dateString?: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSaveVariant = async () => {
    if (!user?.userId) {
      setError("Please log in to save variants");
      return;
    }

    if (!saveName.trim()) {
      setError("Please enter a variant name");
      return;
    }

    if (!hasValidConfiguration) {
      setError("Please fix validation errors before saving");
      return;
    }

    // Check if variant with same name exists
    const existing = variants.find(
      (v) => v.name.toLowerCase() === saveName.trim().toLowerCase()
    );
    if (existing) {
      setShowOverwriteConfirm(existing);
      return;
    }

    await saveVariantToServer(null);
  };

  const saveVariantToServer = async (existingId: string | null) => {
    setIsSaving(true);
    setError("");

    try {
      const userId = user?.userId;
      const payload = {
        user_id: userId,
        name: saveName.trim(),
        description: saveDescription.trim(),
        board_size: currentVariantData.boardSize,
        white_pieces: currentVariantData.whitePieces,
        black_pieces: currentVariantData.blackPieces,
        special_rules: currentVariantData.specialRules,
      };

      const url = existingId
        ? `${API_URL}/api/custom-variants/${existingId}`
        : `${API_URL}/api/custom-variants`;

      const response = await fetch(url, {
        method: existingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to save variant");
      }

      setSuccess(`Variant "${saveName}" saved successfully!`);
      setSaveName("");
      setSaveDescription("");
      onRefresh();

      // Auto-close after success
      setTimeout(() => {
        closeSaveDialog();
      }, 1500);
    } catch (err: any) {
      setError(err.message || "Failed to save variant");
    } finally {
      setIsSaving(false);
      setShowOverwriteConfirm(null);
    }
  };

  const handleOverwriteConfirm = () => {
    if (showOverwriteConfirm) {
      saveVariantToServer(showOverwriteConfirm.id);
    }
  };

  const handleDeleteVariant = async (variantId: string) => {
    try {
      const response = await fetch(`${API_URL}/api/custom-variants/${variantId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete variant");
      }

      onRefresh();
      setShowDeleteConfirm(null);
    } catch (err: any) {
      setNotification({
        isOpen: true,
        type: "error",
        title: "Delete Failed",
        message: err.message || "Failed to delete variant.",
      });
    }
  };

  const handleExportVariant = (variant: CustomVariant) => {
    const exportData = {
      name: variant.name,
      boardSize: variant.boardSize,
      whitePieces: variant.whitePieces,
      blackPieces: variant.blackPieces,
      specialRules: variant.specialRules,
      exportedAt: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${variant.name.replace(/[^a-z0-9]/gi, "_")}_variant.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImportVariant = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      // Validate imported data
      if (!data.name || !data.boardSize || !data.whitePieces || !data.blackPieces) {
        throw new Error("Invalid variant file format");
      }

      // Load the variant into the editor
      onLoadVariant({
        id: "",
        name: data.name,
        boardSize: data.boardSize,
        whitePieces: data.whitePieces,
        blackPieces: data.blackPieces,
        specialRules: data.specialRules,
      });

      setNotification({
        isOpen: true,
        type: "success",
        title: "Variant Imported",
        message: `Variant "${data.name}" has been imported!\n\nReview the configuration and save to keep it.`,
      });
    } catch (err: any) {
      setNotification({
        isOpen: true,
        type: "error",
        title: "Import Failed",
        message: err.message || "Failed to import variant file.",
      });
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const closeSaveDialog = () => {
    setShowSaveDialog(false);
    setSaveName("");
    setSaveDescription("");
    setError("");
    setSuccess("");
  };

  const variantCount = variants.length;
  const remaining = MAX_CUSTOM_VARIANTS - variantCount;

  return (
    <div className="variant-profiles-section">
      <h2>Variant Profiles</h2>
      <p className="section-description">
        Save and load variant configuration profiles ({variantCount}/{MAX_CUSTOM_VARIANTS} saved)
      </p>

      {/* Action Buttons */}
      <div className="variant-config-actions">
        <button
          className="variant-config-btn variant-config-btn-save"
          onClick={() => setShowSaveDialog(true)}
          disabled={!user || remaining <= 0 || !hasValidConfiguration}
          title={
            !user
              ? "Please log in to save variants"
              : remaining <= 0
              ? "Maximum variants reached"
              : !hasValidConfiguration
              ? "Fix validation errors first"
              : "Save current configuration"
          }
        >
          <span className="btn-icon">💾</span> Save Current
        </button>
        <button
          className="variant-config-btn variant-config-btn-import"
          onClick={() => fileInputRef.current?.click()}
        >
          <span className="btn-icon">📥</span> Import
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleImportVariant}
          style={{ display: "none" }}
        />
      </div>

      {/* Saved Variants List */}
      {variants.length > 0 && (
        <div className="saved-variants-list">
          {variants.map((variant) => (
            <div key={variant.id} className="saved-variant-item">
              <div className="variant-info">
                <span className="variant-name">{variant.name}</span>
                <span className="variant-size">
                  {variant.boardSize}×{variant.boardSize}
                </span>
                <span className="variant-date">{formatDate(variant.createdAt)}</span>
              </div>
              <div className="variant-item-actions">
                <button
                  className="variant-item-btn load"
                  onClick={() => onLoadVariant(variant)}
                  title="Load into editor"
                >
                  Load
                </button>
                <button
                  className="variant-item-btn export"
                  onClick={() => handleExportVariant(variant)}
                  title="Export to file"
                >
                  Export
                </button>
                <button
                  className="variant-item-btn delete"
                  onClick={() => setShowDeleteConfirm(variant.id)}
                  title="Delete variant"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {variants.length === 0 && (
        <div className="no-variants-message">
          No saved variants yet. Create a board configuration and save it as a profile.
        </div>
      )}

      {/* Save Dialog */}
      {showSaveDialog && (
        <div className="variant-dialog-overlay" onClick={closeSaveDialog}>
          <div className="variant-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Save Variant Configuration</h3>
            {success ? (
              <>
                <div className="variant-dialog-success">{success}</div>
                <div className="variant-dialog-actions">
                  <button className="variant-dialog-btn save" onClick={closeSaveDialog}>
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="variant-dialog-field">
                  <label>Variant Name *</label>
                  <input
                    type="text"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    placeholder="e.g., Grand Chess 10x10"
                    maxLength={50}
                  />
                </div>
                <div className="variant-dialog-field">
                  <label>Description (optional)</label>
                  <textarea
                    value={saveDescription}
                    onChange={(e) => setSaveDescription(e.target.value)}
                    placeholder="Describe this variant..."
                    maxLength={200}
                    rows={3}
                  />
                </div>
                <div className="variant-dialog-info">
                  Board Size: {currentVariantData.boardSize}×{currentVariantData.boardSize}
                </div>
                {error && <div className="variant-dialog-error">{error}</div>}
                <div className="variant-dialog-actions">
                  <button className="variant-dialog-btn cancel" onClick={closeSaveDialog}>
                    Cancel
                  </button>
                  <button
                    className="variant-dialog-btn save"
                    onClick={handleSaveVariant}
                    disabled={!saveName.trim() || isSaving}
                  >
                    {isSaving ? "Saving..." : "Save Variant"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Overwrite Confirm Dialog */}
      {showOverwriteConfirm && (
        <div className="variant-dialog-overlay" onClick={() => setShowOverwriteConfirm(null)}>
          <div className="variant-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>⚠️ Variant Already Exists</h3>
            <div className="variant-dialog-body">
              <p>
                A variant named <strong>"{showOverwriteConfirm.name}"</strong> already exists.
              </p>
              <p>Do you want to overwrite it?</p>
            </div>
            <div className="variant-dialog-actions">
              <button
                className="variant-dialog-btn cancel"
                onClick={() => setShowOverwriteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="variant-dialog-btn delete"
                onClick={handleOverwriteConfirm}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Overwrite"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Dialog */}
      {showDeleteConfirm && (
        <div className="variant-dialog-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="variant-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>🗑️ Delete Variant</h3>
            <div className="variant-dialog-body">
              <p>
                Are you sure you want to delete{" "}
                <strong>"{variants.find((v) => v.id === showDeleteConfirm)?.name}"</strong>?
              </p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="variant-dialog-actions">
              <button
                className="variant-dialog-btn cancel"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="variant-dialog-btn delete"
                onClick={() => handleDeleteVariant(showDeleteConfirm)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal (replaces native alert dialogs) */}
      <NotificationModal
        isOpen={notification.isOpen}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={() => setNotification(initialNotificationState)}
      />
    </div>
  );
};

export default VariantProfiles;
